import { type AvailableLocale } from '@omnea-digital/toolbox'
import { formatMonetaryAmount } from '@omnea-app/packages/universal/currency-utils/formatCurrency'
import { type CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'

export type NumericUnit = CurrencyCode | null

// `compact` shortens large values (e.g. axis ticks): "$1.2K" instead of "$1,234.00". Shared so a scale
// drawn beside the chart (the bucket-width slider) abbreviates identically.
export const COMPACT_CURRENCY_FORMAT: Intl.NumberFormatOptions = {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
}
export const COMPACT_NUMBER_FORMAT: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 }

// A bound has to read as the value Cube grouped on, so this is the one number formatter that must
// not round — the repo's others all fix the fraction digits.
const EXACT: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 100 }

export const formatNumber = (value: number, unit: NumericUnit, locale: AvailableLocale): string =>
  unit === null ? new Intl.NumberFormat(locale, EXACT).format(value) : formatMonetaryAmount(value, unit, locale, EXACT)

// Intl raises the minimum fraction digits to the maximum when a currency's default (2) exceeds it,
// so the shared compact format reads "£50.0K"; the bucket surfaces want "£50K", as the unitless side reads.
const COMPACT_CURRENCY_LABEL: Intl.NumberFormatOptions = { ...COMPACT_CURRENCY_FORMAT, minimumFractionDigits: 0 }

export const formatNumberAbbreviated = (value: number, unit: NumericUnit, locale: AvailableLocale): string =>
  unit === null
    ? new Intl.NumberFormat(locale, COMPACT_NUMBER_FORMAT).format(value)
    : formatMonetaryAmount(value, unit, locale, COMPACT_CURRENCY_LABEL)

// Compact notation rounds anything under 0.05 to "0", so the sub-unit stops are spelled in full;
// from 1 up the label is the chart axis's own formatting.
export const formatBucketWidth = (w: number, unit: NumericUnit, locale: AvailableLocale): string =>
  w < 1 ? formatNumber(w, unit, locale) : formatNumberAbbreviated(w, unit, locale)
